import { isTMA, requestContact } from '@tma.js/sdk-react';
import { THandleChangeInputPhone, TWidget } from '@libs/types';
import { useEffect, useEffectEvent } from 'react';
import { TConsumerInputPhoneData } from '../types/data';

/**
 * A hook to request the user's phone number from Telegram Mini Apps.
 * If the widget's value is null or undefined and the app is running within a Telegram Mini App,
 * it attempts to request the user's contact information.
 * On success, it updates the widget's value with the received contact data.
 * On failure (e.g., user declines or feature is unsupported), it logs an error.
 *
 * @param widget The widget object for which the phone number is being requested.
 * @param handleChangeInputPhone
 */
export const useGetTelegramPhoneNumber = (widget: TWidget<TConsumerInputPhoneData>, handleChangeInputPhone: THandleChangeInputPhone) => {
    const getItemByPhoneNumber = (phoneNumber?: string) => {
        const data = widget.data;
        return data?.items?.find((i) => {
            const itemValueLength = (i.value as string)?.length;
            const substring = (phoneNumber as string)?.substring?.(0, itemValueLength);
            return i.id === phoneNumber || i.value === substring;
        });
    };

    const checkIsDataValueEmpty = useEffectEvent(() => {
        const value = widget.data?.value as string;
        const item = getItemByPhoneNumber(value);
        return isTMA() && (item?.value === value || value === '');
    });

    const handleGetUserPhoneFromTelegram = useEffectEvent(async () => {
        try {
            const result = await requestContact();

            const userPhoneNumber = result.contact.phone_number;
            const isoCode = getItemByPhoneNumber(userPhoneNumber)?.countryIso;
            handleChangeInputPhone(userPhoneNumber, { countryCode: isoCode }, undefined, userPhoneNumber);
        } catch (error) {
            console.error('User declined or feature unsupported', error);
        }
    });

    useEffect(() => {
        if (checkIsDataValueEmpty()) {
            handleGetUserPhoneFromTelegram().then();
        }
    }, []);
};
